import { GENETIC_COUNSELING, SHARE_RESULTS } from '@constants/routes';
import clinicalReportImage from '@statics/images/home-icons/clinical-report.webp';
import clockImage from '@statics/images/home-icons/clock.webp';
import familyImage from '@statics/images/home-icons/family.webp';
import geneticCounselorImage from '@statics/images/home-icons/genetic-counselor.webp';
import laptopImage from '@statics/images/home-icons/laptop.webp';
import lightbulbImage from '@statics/images/home-icons/lightbulb.webp';
import packageImage from '@statics/images/home-icons/package.webp';
import processImage from '@statics/images/home-icons/process.webp';
import riskResultsImage from '@statics/images/home-icons/risk-results.webp';
import sampleImage from '@statics/images/home-icons/sample.webp';
import wgsImage from '@statics/images/home-icons/wgs.webp';

import type { HomeTimelineStageId } from './homeTimeline';

/**
 * Stage copy from the Patient Portal Homepage Details doc.
 * A resource action is omitted when the doc names no destination.
 */

const ATLAS_URL = 'https://myome.com/our-tests/atlas';
const FAQ_URL = 'https://myome.com/resources/faq';
const COLLECTION_PDF_URL = 'https://myome.com/pdfs/M-004%20v5.3.pdf';
const FEDEX_LOCATOR_URL = 'https://local.fedex.com/en-us';

export interface HomeStageResource {
  id: string;
  icon: string;
  title: string;
  body: string;
  /** Omitted when the doc names no link label. */
  actionLabel?: string;
  /** Omitted where no approved destination exists yet. May carry a `:reqId` the card fills in. */
  href?: string;
}

export interface HomeStageVideo {
  title: string;
  /** YouTube embed URL. Absent when the step has no playable video yet. */
  embedSrc?: string;
}

export interface HomeStageImage {
  src: string;
  alt: string;
}

/** Sentence that ends in the Cures Act early-release action, rendered as one paragraph. */
export interface HomeStageCuresRelease {
  lead: string;
  actionLabel: string;
}

export interface HomeStageDetail {
  /** Panel heading from the homepage details doc. */
  heading?: string;
  body: string[];
  curesRelease?: HomeStageCuresRelease;
  noteHeading?: string;
  notes?: string[];
  closing?: string;
  upNext?: string;
  video?: HomeStageVideo;
  /** Fills the video column on steps that have no video. */
  image?: HomeStageImage;
  resources?: HomeStageResource[];
}

const LEARN_ABOUT_TESTS: HomeStageResource = {
  id: 'learn-about-tests',
  icon: wgsImage,
  title: 'Learn about your tests',
  body: 'Learn what each test screens for and what your results can tell you.',
  actionLabel: 'Explore Atlas',
  href: ATLAS_URL,
};

const FAQS: HomeStageResource = {
  id: 'faqs',
  icon: lightbulbImage,
  title: 'FAQs',
  body: 'Find answers to common questions about sample collection, results, billing, and more.',
  actionLabel: 'View FAQs',
  href: FAQ_URL,
};

const COLLECTION_INSTRUCTIONS: HomeStageResource = {
  id: 'collection-instructions',
  icon: sampleImage,
  title: 'Collection instructions',
  body: 'Get step-by-step instructions for collecting your sample.',
  actionLabel: 'View PDF',
  href: COLLECTION_PDF_URL,
};

// Atlas stands in for a per-product guide until one exists.
const UNDERSTANDING_RESULTS: HomeStageResource = {
  id: 'understanding-results',
  icon: riskResultsImage,
  title: 'Understanding your results',
  body: 'Results guides can be found for each product included in your order.',
  actionLabel: 'Search products here',
  href: ATLAS_URL,
};

const OVERVIEW_VIDEO: HomeStageVideo = {
  title: 'MyOme Overview video',
  embedSrc: 'https://www.youtube.com/embed/pl9kB62u80I',
};

const CLOCK_IMAGE: HomeStageImage = {
  src: clockImage,
  alt: 'Clock',
};

const PROCESS_IMAGE: HomeStageImage = {
  src: processImage,
  alt: 'Review steps',
};

const CLINICAL_REPORT_IMAGE: HomeStageImage = {
  src: clinicalReportImage,
  alt: 'Clinical report',
};

export const HOME_STAGE_DETAIL: Record<HomeTimelineStageId, HomeStageDetail> = {
  'order-placed': {
    heading: 'We received your order',
    body: [
      "Thank you for choosing MyOme. Your order has been received and is being reviewed by a licensed medical professional. Reviews typically take 24-48 hours, and you will receive a notification once it's complete.",
      "Once your order has been reviewed and approved, we'll ship you a collection kit so that you can provide a sample for testing.",
    ],
    upNext: 'Your order will be reviewed in 24-48 hours',
    video: OVERVIEW_VIDEO,
    resources: [LEARN_ABOUT_TESTS, FAQS],
  },

  'order-approved': {
    heading: 'A collection kit was shipped to you',
    body: [
      'Your order has been approved! Your collection kit is on its way and should arrive in 3 to 5 business days. When it arrives, follow the enclosed instructions and send your sample back as soon as possible.',
    ],
    upNext: 'Your collection kit arrives in 3-5 business days',
    video: OVERVIEW_VIDEO,
    resources: [COLLECTION_INSTRUCTIONS, LEARN_ABOUT_TESTS, FAQS],
  },

  'awaiting-sample': {
    heading: 'Send your sample to MyOme',
    body: [
      'A collection kit was sent to you for easy at-home sample collection. Follow the instructions to collect your sample and send it back to us using the included pre-paid return label.',
    ],
    noteHeading: 'Important things to note',
    notes: [
      'Do not eat, drink, smoke, or chew gum for at least 30 minutes prior to collection',
      'Use one swab per cheek and rub along lower gums 10 times',
      'Store at room temperature and ship within 1 week of collection',
      "Label each tube with the patient's full name, date of birth, and collection date",
    ],
    closing:
      "If you did not receive your collection kit, need a replacement kit, or have any other questions, we're here to help!",
    upNext: 'Return your sample and we will process it in our lab',
    // DNA Genotek's collection video until MyOme has its own.
    video: { title: 'Sample collection video', embedSrc: 'https://www.youtube.com/embed/ulwe9tvhTqI' },
    resources: [
      COLLECTION_INSTRUCTIONS,
      {
        id: 'collection-faqs',
        icon: laptopImage,
        title: 'Sample collection FAQs',
        body: 'Get answers to questions about collection and shipping logistics.',
        actionLabel: 'View FAQs',
        href: FAQ_URL,
      },
      {
        id: 'fedex-locator',
        icon: packageImage,
        title: 'Find a FedEx dropoff location',
        body: 'Search for nearby locations to drop off your sample using the pre-paid return label.',
        actionLabel: 'View locations',
        href: FEDEX_LOCATOR_URL,
      },
    ],
  },

  /** Doc status is Analysis. Sample Received is not a rail step. */
  analysis: {
    heading: 'Your order is processing',
    body: [
      "We received your sample and it's processing in the lab. What this actually means is your sample gets sequenced, the data is analyzed and interpreted by our team, and a report is generated.",
      'On average, it takes 5-6 weeks from the day we received your sample to deliver results.',
      'Once results are ready they will be sent to a licensed medical professional for review and to generate a summary letter before they are shared with you.',
    ],
    upNext: 'You will be notified when your results are ready',
    image: CLOCK_IMAGE,
    resources: [UNDERSTANDING_RESULTS, FAQS],
  },

  'clinician-review': {
    heading: 'Your results are being reviewed',
    body: [
      'A licensed medical professional is reviewing your results and writing a summary letter to go with your results. You will receive an email once your results are ready for your review. You may also be contacted by our third-party partner, DNAvist to schedule a genetic counseling appointment.',
    ],
    curesRelease: {
      lead: 'We strongly recommend waiting until your results have been reviewed and released to you, but in compliance with the 21st Century Cures Act, results can be made immediately available upon request.',
      actionLabel: 'Request results here.',
    },
    upNext: "You'll receive an email when results are available",
    image: PROCESS_IMAGE,
    resources: [UNDERSTANDING_RESULTS, FAQS],
  },

  'results-ready': {
    heading: 'Your results are ready',
    body: [
      'Your results are now available. You can learn more about what your results mean, recommend next steps, and download a PDF of your results all in the "My Results" section of your portal.',
      'Additionally, you can email a copy of your results to your healthcare provider and/or family member(s) and schedule a genetic counseling appointment at no additional cost with our third-party partner, DNAvisit.',
    ],
    upNext: 'Review your results',
    image: CLINICAL_REPORT_IMAGE,
    resources: [
      UNDERSTANDING_RESULTS,
      {
        id: 'share-results',
        icon: familyImage,
        title: 'Share results',
        body: 'Email a copy of your results to your provider and/or family member(s).',
        actionLabel: 'Share results',
        href: SHARE_RESULTS,
      },
      {
        id: 'schedule-counseling',
        icon: geneticCounselorImage,
        title: 'Schedule genetic counseling',
        body: 'Talk through your results with a certified genetic counselor at DNAvisit.',
        actionLabel: 'Schedule now',
        href: GENETIC_COUNSELING,
      },
    ],
  },
};

export const HOME_STAGE_DETAIL_COPY = {
  /** mockup: eyebrow above the next-step tile. */
  upNextLabel: 'Up next',
  /** mockup: heading above the per-stage resource cards. */
  resourcesTitle: 'Resources for this step',
} as const;
